/**
 * dockSettingsParser.js
 * Parses a raw calendar API response (`{ event: [...], exceptionEvent: [...] }`)
 * into the settings shape DockDoorSettings expects: regularHours, holidays,
 * blackouts, and recurringEvents. Shared by CalendarSettings.js and the
 * read-only ViewLocation views (Locations and Manage-Team features) — was
 * previously duplicated 3x and had already drifted (the ViewLocation copies
 * hardcoded the holiday year instead of deriving it from startDate).
 */

const capitalizeFirstLetter = (str) =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

export const parseCalendarApiResponseToDockSettings = (apiResponse) => {
  const regularHours = {};
  const holidays = [];
  const blackouts = [];
  const recurringEvents = [];

  for (const evt of apiResponse?.event || []) {
    const recurringType = (evt.event_recurring_type || evt.recurringType || "").toUpperCase();
    const statusType = (
      evt.event_statue_type || evt.eventStatusType || evt.eventTypeName || ""
    ).toUpperCase();

    if (!recurringType || !statusType) continue;

    // Regular hours: WEEKLY + OPEN, single day per row (unchanged).
    if (recurringType === "WEEKLY" && statusType === "OPEN") {
      const day = evt.dayOfWeek && capitalizeFirstLetter(evt.dayOfWeek);
      if (day) {
        regularHours[day] = {
          from: evt.startTime?.slice(0, 5) || "",
          to: evt.endTime?.slice(0, 5) || "",
          eventId: evt.eventId,
        };
      }
      continue;
    }

    // Holidays: YEARLY + CLOSE + whole-day (no start/end time).
    if (recurringType === "YEARLY" && statusType === "CLOSE" && evt.isWholeDay !== false && !evt.startTime) {
      const month = evt.monthOfYear?.toString().padStart(2, "0");
      const day = evt.dayOfMonth?.toString().padStart(2, "0");
      if (month && day) {
        const baseDate = evt.startDate || evt.start_date;
        const parsedBase = baseDate ? new Date(baseDate) : null;
        const year =
          parsedBase && !isNaN(parsedBase.getTime())
            ? parsedBase.getFullYear()
            : new Date().getFullYear();
        holidays.push({
          date: `${year}-${month}-${day}`,
          allDay: true,
          reason: evt.eventDescription,
          eventId: evt.eventId,
        });
      }
      continue;
    }

    // Everything else CLOSE (DAILY, MONTHLY, WEEKLY+CLOSE, or a time-bounded
    // YEARLY row) is a Recurring Event.
    if (statusType === "CLOSE") {
      const isMonthlyByWeekday = recurringType === "MONTHLY" && evt.weekOfMonth != null;

      // The singular dayOfWeek column means different things depending on
      // pattern: for Weekly (no daysOfWeek array) it's a single selected day;
      // for Monthly day-of-week mode it's "which weekday" paired with weekOfMonth.
      const daysOfWeek =
        recurringType === "WEEKLY"
          ? Array.isArray(evt.daysOfWeek)
            ? evt.daysOfWeek
            : evt.dayOfWeek
            ? [evt.dayOfWeek.toUpperCase()]
            : []
          : [];

      recurringEvents.push({
        recurringEventId: evt.eventId,
        eventName: evt.eventDescription || "",
        allDay: evt.isWholeDay === true,
        startTime: evt.startTime?.slice(0, 5) || "",
        endTime: evt.endTime?.slice(0, 5) || "",
        recurrencePattern: recurringType,
        intervalCount: evt.intervalCount || 1,
        daysOfWeek,
        monthlyMode: isMonthlyByWeekday ? "DAY_OF_WEEK" : "DAY_OF_MONTH",
        weekOfMonth: isMonthlyByWeekday ? evt.weekOfMonth : null,
        monthlyDayOfWeek: isMonthlyByWeekday ? (evt.dayOfWeek || "").toUpperCase() : null,
        dayOfMonth: evt.dayOfMonth ?? null,
        monthOfYear: evt.monthOfYear ?? null,
        effectiveDate: evt.startDate || evt.start_date || "",
        // The backend stores "recurs indefinitely" as a null end date; the UI makes
        // that an explicit choice rather than an empty field.
        endMode: evt.endDate ? "ON_DATE" : "INDEFINITELY",
        endDate: evt.endDate || null,
      });
    }
  }

  for (const ex of apiResponse?.exceptionEvent || []) {
    if (ex.recurringEventId != null) continue;
    if ((ex.overrideType || "").toUpperCase() === "CLOSE") {
      blackouts.push({
        from: ex.startDate,
        to: ex.endDate || ex.startDate,
        allDay: ex.isWholeDay === true,
        reason: ex.reason,
        exceptionId: ex.exceptionId,
      });
    }
  }

  return {
    regularHours,
    holidays,
    blackouts,
    recurringEvents,
    appointmentDuration: apiResponse?.appointmentDuration,
    calendarId: apiResponse?.calendarId,
    calendarRefId: apiResponse?.calendarRefId,
  };
};
